import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { Modal } from 'antd';

import { DocumentsFilters } from '@totem/components/documents/filters';
import {
  documentsContainerConfiguration,
  NO_REFERENCE_LABEL,
} from '@totem/components/documents/report/containerConfiguration';
import DocumentsContext from '@totem/components/documents/report/documentsContext';
const confirm = Modal.confirm;
import DocumentEditDialog from '@totem/components/documents/dialogs/documentEditDialog';
import {
  DocumentReference,
  DocumentsSummary,
  DocumentSummaryEntry,
  DocumentSummaryResponse,
} from '@totem/components/documents/types';
import DocumentsLinkDialog from '@totem/components/documents/upload/documentsLinkDialog';
import DocumentsMergedDialog from '@totem/components/documents/upload/documentsMergedDialog';
import {
  addDocumentFilters,
  creatorLabel,
  escapeRegex,
  fileTypeLabel,
} from '@totem/components/documents/utilities';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Filter, Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { DOCUMENTS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';
// Export pulls every matching document in one request.
const EXPORT_PAGE_SIZE = 10000;

type Props = {
  staticFilters?: DocumentsFilters;
  documentReference?: DocumentReference;
  documentReferenceInternal?: boolean;
  // Persists the user's filters, sort and paging; omit for embedded document lists.
  componentKey?: string;
  defaultSort?: { field: string; direction: string };
  children?: ReactNode;
};

// Turns the user's column filters into what the documents endpoint expects.
const toServerFilters = (filters: Filter[]): Filter[] =>
  filters.map((filter) => {
    if (filter.field === 'displayName') {
      return { ...filter, values: filter.values.map(escapeRegex) };
    }
    if (filter.field === 'objectType') {
      return {
        ...filter,
        values: filter.values.flatMap((value) =>
          value === NO_REFERENCE_LABEL ? ['', 'None'] : [value],
        ),
      };
    }
    return filter;
  });

const DocumentsDataContainer = ({
  children,
  staticFilters,
  documentReference,
  documentReferenceInternal,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { regions, buildings, controlSystems } =
    useContext(OrganizationContext);
  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [data, setData] = useState<DocumentSummaryEntry[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>();
  const [filters, setFilters] = useState<Filter[]>([]);
  const [action, setAction] = useState<string>('');
  const [actionItem, setActionItem] = useState<DocumentSummaryEntry>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<
    DocumentSummaryEntry[]
  >([]);
  const [summary, setSummary] = useState<DocumentsSummary | null>(null);
  const [summaryCount, setSummaryCount] = useState<number>(0);

  useEffect(() => {
    const toOptions = (items: { id: string; name: string }[]) =>
      [...(items ?? [])]
        .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
        .map((item) => ({ text: item.name, value: item.id }));

    setFilterOptions('region', toOptions(regions));

    setFilterOptions('building', toOptions(buildings));

    setFilterOptions('controlSystem', toOptions(controlSystems));
  }, [regions, buildings, controlSystems]);

  // The summary supplies the KPI counts and the Ref Type, Type and Added By filter options. Reloaded after
  // uploads, edits and deletes.
  useEffect(() => {
    fetch(`${DOCUMENTS_ENDPOINT}/summary`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({
        filters: addDocumentFilters([], staticFilters, documentReference),
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: DocumentsSummary) => {
        setSummary(result);

        setFilterOptions(
          'refType',
          [...(result.byReference ?? [])]
            .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
            .map((entry) => ({ text: entry.name, value: entry.name })),
        );

        setFilterOptions(
          'type',
          [...(result.byFileType ?? [])]
            .map((entry) => ({
              text: fileTypeLabel(entry.name),
              value: entry.name,
            }))
            .sort((chkA, chkB) => sortStringAscending(chkA.text, chkB.text)),
        );

        setFilterOptions(
          'createdAt',
          [...(result.byCreator ?? [])]
            .map((entry) => ({
              text: creatorLabel(entry.name),
              value: entry.name,
            }))
            .sort((chkA, chkB) => sortStringAscending(chkA.text, chkB.text)),
        );
      })
      .catch(() => {
        setSummary(null);

        setFilterOptions('refType', []);
      });
  }, [userProfile.organization.id, getApiToken, summaryCount]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [
    isReady,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    userProfile.organization.id,
  ]);

  const buildServerParameters = (): Params => {
    const params: Params = buildParameters();
    params.filters = addDocumentFilters(
      toServerFilters(params.filters),
      staticFilters,
      documentReference,
    );
    return params;
  };

  const fetchAllDocuments = () =>
    fetch(`${DOCUMENTS_ENDPOINT}`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({
        ...buildServerParameters(),
        paging: { page: 1, pageSize: EXPORT_PAGE_SIZE },
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: DocumentSummaryResponse) => result.results ?? []);

  useEffect(() => {
    if (isReady && refreshData) {
      setRefreshData(false);

      setLoading(true);
      const params = buildServerParameters();

      setFilters(params.filters);

      fetch(`${DOCUMENTS_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: DocumentSummaryResponse) => {
          setTotalRecords(result.paging.totalRecords);

          setData(result.results);
        })
        .then(() => {
          setLoading(false);
        });
    }
  }, [refreshData, isReady, getApiToken]);
  const setDocumentSelected = (
    docSelected: DocumentSummaryEntry,
    selected: boolean,
  ) => {
    if (selected) {
      if (
        selectedDocuments.findIndex(
          (chk) => chk.document.id === docSelected.document.id,
        ) <= -1
      ) {
        setSelectedDocuments([...selectedDocuments, docSelected]);
      }
    } else {
      const filtered = selectedDocuments.filter(
        (chk) => chk.document.id !== docSelected.document.id,
      );
      setSelectedDocuments(filtered);
    }
  };
  const handleHardDelete = (docToDelete: DocumentSummaryEntry) => {
    confirm({
      title: 'Are you sure?',
      content: `Please confirm that you would like to permanently remove the document ${docToDelete.document.displayName}.  This action cannot be undone!`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      visible: false,
      onOk: () => {
        fetch(`${DOCUMENTS_ENDPOINT}/${docToDelete.document.id}`, {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        })
          .then((res) => {
            CheckResponseShowError(res);
          })
          .then(() => {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            handleAction('refresh', null);
          });
      },
    });
  };
  const handleAction = (newAction: string, docEntry: DocumentSummaryEntry) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);

        setSummaryCount((count) => count + 1);

        setAction('');

        setActionItem(null);
        break;
      case 'hard-delete':
        setAction('');

        setActionItem(null);

        handleHardDelete(docEntry);
        break;
      default:
        setAction(newAction);

        setActionItem(docEntry);
        break;
    }
  };
  return (
    <DocumentsContext.Provider
      value={{
        loading,
        data,
        totalRecords,
        action,
        actionItem,
        onAction: handleAction,
        filters,
        selectedDocuments,
        setDocumentSelected,
        documentReference,
        documentReferenceInternal: isNotNull(documentReferenceInternal)
          ? documentReferenceInternal
          : false,
        summary,
        fetchAllDocuments,
      }}
    >
      <div>{children}</div>
      {action === 'upload' && (
        <DocumentsMergedDialog
          onClose={(refresh) =>
            refresh ? handleAction('refresh', null) : handleAction('', null)
          }
          visible={action === 'upload'}
          reference={documentReference}
        />
      )}
      {action === 'link' && (
        <DocumentsLinkDialog
          onClose={(refresh) =>
            refresh ? handleAction('refresh', null) : handleAction('', null)
          }
          reference={documentReference}
          visible={action === 'link'}
        />
      )}
      {action === 'edit' && (
        <DocumentEditDialog
          document={actionItem}
          onClose={(refresh) =>
            refresh ? handleAction('refresh', null) : handleAction('', null)
          }
          visible={action === 'edit'}
        />
      )}
    </DocumentsContext.Provider>
  );
};

const DocumentsContainer = ({
  componentKey,
  defaultSort = { field: 'document.displayName', direction: 'ascend' },
  ...props
}: Props) => (
  <InputContainer
    filterConfig={documentsContainerConfiguration}
    defaultSort={defaultSort}
    componentKey={componentKey}
  >
    <DocumentsDataContainer {...props} />
  </InputContainer>
);

export default DocumentsContainer;
